import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
} from 'chart.js';
import { Line, Bar, Pie, Doughnut } from 'react-chartjs-2';
import './Reports.css';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement
);

const Reports = ({ onBack, isSuperUser, companySlug: userCompanySlug }) => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedScope, setSelectedScope] = useState(isSuperUser ? 'combined' : 'company');
    const [companies, setCompanies] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState(userCompanySlug || '');

    // New Filters
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState('');
    const [accounts, setAccounts] = useState([]);
    const [selectedAccount, setSelectedAccount] = useState('');
    const [pdfGenerating, setPdfGenerating] = useState(false);
    const trendChartRef = useRef(null);
    const productChartRef = useRef(null);
    const companyChartRef = useRef(null);

    useEffect(() => {
        if (isSuperUser) {
            fetchCompanies();
        }
    }, [isSuperUser]);

    useEffect(() => {
        fetchUsers();
        fetchAccounts();
    }, [selectedScope, selectedCompany]);

    useEffect(() => {
        fetchStats();
    }, [selectedScope, selectedCompany]);

    const fetchCompanies = async () => {
        try {
            const res = await axios.get('/api/companies');
            if (res.data?.success) {
                setCompanies(res.data.companies || []);
            }
        } catch (err) {
            console.error('Failed to fetch companies', err);
        }
    };

    const fetchUsers = async () => {
        try {
            const params = {
                scope: selectedScope,
                companySlug: selectedScope === 'company' ? selectedCompany : undefined
            };
            const res = await axios.get('/api/reports/users', { params });
            if (res.data?.success) {
                // Return just the email strings for simplicity if Backend returns objects
                const userList = (res.data.users || []).map(u => typeof u === 'string' ? u : u.email);
                setUsers(userList);
            }
        } catch (err) {
            console.error('Failed to fetch users', err);
        }
    };

    const fetchAccounts = async () => {
        try {
            const params = {
                scope: selectedScope,
                companySlug: selectedScope === 'company' ? selectedCompany : undefined
            };
            const res = await axios.get('/api/reports/accounts', { params });
            if (res.data?.success) {
                setAccounts(res.data.accounts || []);
            }
        } catch (err) {
            console.error('Failed to fetch accounts', err);
        }
    };

    const fetchStats = async () => {
        setLoading(true);
        try {
            const params = {
                scope: selectedScope,
                companySlug: selectedScope === 'company' ? selectedCompany : undefined,
                startDate,
                endDate,
                user: selectedUser,
                account: selectedAccount
            };
            const res = await axios.get('/api/reports/stats', { params });
            if (res.data?.success) {
                setStats(res.data);
                setError(null);
            } else {
                setError(res.data?.message || 'Failed to load report data');
            }
        } catch (err) {
            setError('Error fetching report data. Please try again.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const downloadPDF = async () => {
        if (pdfGenerating) return;
        setPdfGenerating(true);
        try {
            const token = localStorage.getItem('authToken');
            const params = new URLSearchParams({
                scope: selectedScope,
                companySlug: selectedScope === 'company' ? selectedCompany : '',
                startDate,
                endDate,
                user: selectedUser,
                account: selectedAccount
            });

            const url = `/api/reports/summary/pdf?${params.toString()}`;

            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(errorText || 'Failed to generate PDF');
            }

            const blob = await response.blob();
            if (blob.type === 'application/json' || blob.type.includes('text')) {
                // Highly likely an error encoded as JSON
                const text = await blob.text();
                try {
                    const err = JSON.parse(text);
                    throw new Error(err.message || 'Server error');
                } catch (e) {
                    throw new Error(text || 'Server error');
                }
            }

            const blobUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = `Shipping_Report_${new Date().toISOString().split('T')[0]}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            console.error('Failed to download PDF', err);
            alert(`Error generating PDF report: ${err.message}`);
        } finally {
            setPdfGenerating(false);
        }
    };


    const trendData = useMemo(() => {
        if (!stats?.salesTrend) return null;
        return {
            labels: stats.salesTrend.map(s => s.month),
            datasets: [
                {
                    label: 'Sales (USD)',
                    data: stats.salesTrend.map(s => s.usd),
                    borderColor: '#3b82f6',
                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    fill: true,
                    tension: 0.4,
                }
            ]
        };
    }, [stats]);

    const productData = useMemo(() => {
        if (!stats?.productDistribution) return null;
        const entries = Object.entries(stats.productDistribution);
        return {
            labels: entries.map(([k]) => k),
            datasets: [
                {
                    data: entries.map(([, v]) => v),
                    backgroundColor: [
                        '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#6366f1'
                    ],
                }
            ]
        };
    }, [stats]);

    const companyData = useMemo(() => {
        if (!stats?.companyDistribution) return null;
        const entries = Object.entries(stats.companyDistribution);
        return {
            labels: entries.map(([k]) => k),
            datasets: [
                {
                    label: 'Sales (PKR)',
                    data: entries.map(([, v]) => v),
                    backgroundColor: '#8b5cf6',
                }
            ]
        };
    }, [stats]);

    if (loading && !stats) return <div className="reports-loading">Analyzing Data...</div>;

    return (
        <div className="reports-container animated fadeIn">
            <header className="reports-header">
                <div className="header-left">
                    <button className="back-btn" onClick={onBack}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
                        Back
                    </button>
                    <h1>Reporting Analytics</h1>
                </div>

                <div className="filters">
                    <div className="filter-group">
                        <label>Scope</label>
                        <select value={selectedScope} onChange={(e) => setSelectedScope(e.target.value)}>
                            {isSuperUser && <option value="combined">Combined (All)</option>}
                            {isSuperUser && <option value="global">Global (ULS Main Only)</option>}
                            <option value="company">Specific Company</option>
                        </select>
                    </div>

                    {selectedScope === 'company' && (
                        <div className="filter-group">
                            <label>Company</label>
                            <select
                                value={selectedCompany}
                                onChange={(e) => setSelectedCompany(e.target.value)}
                                disabled={!isSuperUser}
                            >
                                <option value="">Select Company</option>
                                {isSuperUser ? (
                                    companies.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)
                                ) : (
                                    <option value={userCompanySlug}>{userCompanySlug}</option>
                                )}
                            </select>
                        </div>
                    )}

                    <div className="filter-group">
                        <label>Date Range</label>
                        <div className="date-inputs">
                            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} title="Start Date" />
                            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} title="End Date" />
                        </div>
                    </div>

                    <div className="filter-group">
                        <label>User (Filter)</label>
                        <input
                            list="report-users-list"
                            className="search-input"
                            value={selectedUser}
                            onChange={(e) => setSelectedUser(e.target.value)}
                            placeholder="Search/Select User"
                        />
                        <datalist id="report-users-list">
                            {users.map(u => <option key={u} value={u} />)}
                        </datalist>
                    </div>

                    <div className="filter-group">
                        <label>Account</label>
                        <select
                            value={selectedAccount}
                            onChange={(e) => setSelectedAccount(e.target.value)}
                        >
                            <option value="">All Accounts</option>
                            {accounts.map(acc => <option key={acc} value={acc}>{acc}</option>)}
                        </select>
                    </div>

                    <button className="refresh-btn" onClick={fetchStats} title="Refresh Data">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" /></svg>
                    </button>

                    <button
                        className={`download-btn ${pdfGenerating ? 'loading' : ''}`}
                        onClick={downloadPDF}
                        disabled={pdfGenerating}
                        title="Download PDF Report"
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" /></svg>
                        {pdfGenerating ? 'Generating...' : 'PDF'}
                    </button>

                    {(startDate || endDate || selectedUser || selectedAccount) && (
                        <button className="clear-filters" onClick={() => {
                            setStartDate('');
                            setEndDate('');
                            setSelectedUser('');
                            setSelectedAccount('');
                        }} title="Clear all filters">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
                        </button>
                    )}
                </div>

            </header>

            {error && <div className="error-alert">{error}</div>}

            <div className="stats-grid">
                <div className="stat-card primary">
                    <span className="label">Total Sales (USD)</span>
                    <h2 className="value">${stats?.summary?.totalUSD.toLocaleString(undefined, { minimumFractionDigits: 2 })}</h2>
                    <span className="sub">Includes all shipments & FC</span>
                </div>
                <div className="stat-card success">
                    <span className="label">Total Sales (PKR)</span>
                    <h2 className="value">Rs. {stats?.summary?.totalPKR.toLocaleString(undefined, { minimumFractionDigits: 0 })}</h2>
                    <span className="sub">Cleaned aggregation</span>
                </div>
                <div className="stat-card info">
                    <span className="label">Total Shipments</span>
                    <h2 className="value">{stats?.summary?.totalShipments.toLocaleString()}</h2>
                    <span className="sub">{stats?.summary?.activeCount} Active / {stats?.summary?.voidedCount} Voided</span>
                </div>
                <div className="stat-card urgent">
                    <span className="label">Total Weight</span>
                    <h2 className="value">{stats?.summary?.totalWeight?.toFixed(2)} KGS</h2>
                    <span className="sub">Active shipments only</span>
                </div>
                <div className="stat-card warning">
                    <span className="label">FC Labels</span>
                    <h2 className="value">{stats?.summary?.fcCount}</h2>
                    <span className="sub">${stats?.summary?.fcUSD.toLocaleString()} Revenue</span>
                </div>
            </div>

            <div className="charts-main-grid">
                <div className="chart-item line-chart-wide">
                    <h3>Monthly Sales Trend</h3>
                    {trendData ? <Line ref={trendChartRef} data={trendData} options={{ responsive: true, maintainAspectRatio: false }} /> : <p>No trend data available</p>}
                </div>

                <div className="chart-sub-grid">
                    <div className="chart-item">
                        <h3>Product Distribution</h3>
                        {productData ? <Doughnut ref={productChartRef} data={productData} options={{ responsive: true, maintainAspectRatio: false }} /> : <p>No data</p>}
                    </div>
                    <div className="chart-item">
                        <h3>Sales by Company</h3>
                        {companyData ? <Bar ref={companyChartRef} data={companyData} options={{ responsive: true, maintainAspectRatio: false }} /> : <p>No data</p>}
                    </div>
                </div>
            </div>

            <div className="bottom-sections">
                <div className="top-sellers-section">
                    <h3>Top 10 Sellers (by Revenue)</h3>
                    <div className="table-responsive">
                        <table>
                            <thead>
                                <tr>
                                    <th>Rank</th>
                                    <th>User ID</th>
                                    <th>Weight (KGS)</th>
                                    <th>Total Revenue (PKR)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {stats?.topSellers?.length > 0 ? (
                                    stats.topSellers.map((s, idx) => (
                                        <tr key={idx}>
                                            <td><span className={`rank rank-${idx + 1}`}>{idx + 1}</span></td>
                                            <td>{s.user}</td>
                                            <td>{s.weight?.toFixed(2)} KGS</td>
                                            <td>Rs. {s.pkr.toLocaleString()}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr><td colSpan="3">No seller data found</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Reports;
